import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, User, Package, MessageCircle, LogOut, ChevronDown, Settings, Plus } from 'lucide-react';
import { useAuth } from '../App';
import CoinLogo from './CoinLogo';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-surface-base/80 border-b border-border-subtle">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo">
            <CoinLogo size={36} animate={false} pauseOnHover={false} />
            <span className="font-clash text-lg font-semibold gold-text hidden sm:inline">
              CoinHustle Exchange
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link 
              to="/marketplace" 
              className="text-sm text-zinc-400 hover:text-white transition-colors"
              data-testid="nav-marketplace"
            >
              Marketplace
            </Link>
            <Link 
              to="/wanted" 
              className="text-sm text-zinc-400 hover:text-white transition-colors"
              data-testid="nav-wanted"
            >
              Wanted
            </Link>
            <Link 
              to="/learn" 
              className="text-sm text-zinc-400 hover:text-white transition-colors"
              data-testid="nav-learn"
            >
              Learn
            </Link>
            {user && (
              <Link 
                to="/dashboard" 
                className="text-sm text-zinc-400 hover:text-white transition-colors"
                data-testid="nav-dashboard"
              >
                Dashboard
              </Link>
            )}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-4">
            {user ? (
              <>
                <Link
                  to="/create-listing"
                  className="hidden sm:flex btn-gold px-4 py-2 text-sm items-center gap-2"
                  data-testid="create-listing-header-btn"
                >
                  <Plus className="w-4 h-4" /> Sell
                </Link>

                <Link 
                  to="/messages" 
                  className="relative p-2 text-zinc-400 hover:text-white transition-colors"
                  data-testid="nav-messages"
                >
                  <MessageCircle className="w-5 h-5" />
                </Link>

                <DropdownMenu>
                  <DropdownMenuTrigger className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors" data-testid="user-menu-trigger">
                    <div className="w-8 h-8 bg-gold/10 flex items-center justify-center">
                      {user.avatar_url ? (
                        <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-4 h-4 text-gold" />
                      )}
                    </div>
                    <ChevronDown className="w-4 h-4 hidden sm:block" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-surface-card border-border-subtle">
                    <div className="px-3 py-2">
                      <p className="text-sm font-medium text-white">{user.name}</p>
                      <p className="text-xs text-zinc-400">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator className="bg-border-subtle" />
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer">
                        <Package className="w-4 h-4" /> Dashboard
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="flex items-center gap-2 cursor-pointer">
                        <User className="w-4 h-4" /> Profile
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/messages" className="flex items-center gap-2 cursor-pointer">
                        <MessageCircle className="w-4 h-4" /> Messages
                      </Link>
                    </DropdownMenuItem>
                    {user.role === 'admin' && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center gap-2 cursor-pointer">
                          <Settings className="w-4 h-4" /> Admin
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator className="bg-border-subtle" />
                    <DropdownMenuItem 
                      onClick={handleLogout}
                      className="flex items-center gap-2 cursor-pointer text-red-400"
                      data-testid="logout-btn"
                    >
                      <LogOut className="w-4 h-4" /> Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <Link
                  to="/login"
                  className="text-sm text-zinc-400 hover:text-white transition-colors"
                  data-testid="login-btn"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="btn-gold px-4 py-2 text-sm"
                  data-testid="register-btn"
                >
                  Get Started
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-zinc-400 hover:text-white"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border-subtle">
            <nav className="flex flex-col gap-2">
              <Link 
                to="/marketplace" 
                className="px-4 py-2 text-zinc-400 hover:text-white hover:bg-surface-card transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Marketplace
              </Link>
              <Link 
                to="/wanted" 
                className="px-4 py-2 text-zinc-400 hover:text-white hover:bg-surface-card transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Wanted
              </Link>
              <Link 
                to="/learn" 
                className="px-4 py-2 text-zinc-400 hover:text-white hover:bg-surface-card transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Learn
              </Link>
              {user && (
                <>
                  <Link 
                    to="/dashboard" 
                    className="px-4 py-2 text-zinc-400 hover:text-white hover:bg-surface-card transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <Link 
                    to="/create-listing" 
                    className="px-4 py-2 text-gold hover:bg-surface-card transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    + Create Listing
                  </Link>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
